import React, { useEffect, useState } from "react";
import { TIconType } from "../../component";
import ZFMultiStyleCard from "../../ui-component/ZFMultiStyleCard/ZFMultiStyleCard";
import {
	IColumnObject,
	IGlobalConfigObject,
	IMetaCardConfig,
	IMetaCardProps,
	IMetaCardTab,
	ISuffixPrefix,
	ITransformedColumn,
	ValueType,
} from "./CustomizedMetaCard.d";
import { get as lodashGet, chunk } from "lodash";

const RED_CLASS = "zf-text-color-red-acc";
const GREEN_CLASS = "zf-text-color-green-acc";

const RED_DOWN_ICON = "red-ind" as TIconType;
const GREEN_UP_ICON = "green-ind" as TIconType;

const PLACE_HOLDER = "--";
const EMPTY_SPACE = " ";

const SECONDARY_ICON_CLASS = "zf-icon-react--size-s";

const KEYS_WITH_DIFFERENT_DEFINITION = [
	"wip",
	"dhu",
	"runningStyle",
	"manHour",
];

export const CustomizedMetaCard: React.FC<IMetaCardProps> = ({
	config,
	data,
}: IMetaCardProps) => {
	const [activeTabIndex, updateActiveTabIndex] = useState(0);
	const [activeMetaConfig, updateActiveMetaConfig] = useState({
		globalConfig: [],
		multiStyleCardConfig: [],
	} as MultiStyleCardConfig);

	useEffect(() => {
		setView(activeTabIndex, data);
		console.log("test");
	}, [data]);

	const onTabChange = (i: number) => {
		if (i !== activeTabIndex) {
			setView(i, data);
			updateActiveTabIndex(i);
		}
	};

	const setView = (index: number, dataInit) => {
		const activeTab = config.tabs[index];
		if (activeTab) {
			const activeTabData = lodashGet(
				dataInit,
				activeTab.dataPath,
				dataInit
			);
			const activeMetaConfig = transformMetaConfig(
				activeTab,
				activeTabData
			);
			updateActiveMetaConfig(activeMetaConfig);
		}
	};

	return (
		<div className="customized-meta-card zf-padding-xl">
			{config && config.tabs && config.tabs.length > 1 && (
				<div style={{ overflowX: "auto" }}>
					<div className="tab-group">
						{config.tabs.map((tab, i) => {
							return (
								<div
									onClick={() => onTabChange(i)}
									className={
										"tab " +
										(i === activeTabIndex
											? "active-tab"
											: "")
									}
								>
									<div
										className="header-legend"
										style={{ backgroundColor: tab.legend }}
									></div>
									<div>{tab.value.toUpperCase()}</div>
								</div>
							);
						})}
					</div>
				</div>
			)}

			<ZFMultiStyleCard config={activeMetaConfig} />
		</div>
	);
};

const getTransformedColumn = (
	obj: ISuffixPrefix,
	data: object
): ITransformedColumn => {
	let icon,
		finalClass = obj.class ?? "",
		thClass = "";

	let value = obj.key ? data[obj.key] ?? PLACE_HOLDER : obj.value;

	const displayType = obj.displayType ?? "PRIMARY";

	if (displayType === "SECONDARY") {
		finalClass +=
			EMPTY_SPACE +
			"zf-text-font-size-s zf-text-line-height-xs zf-text-color-black-80";
	}

	if (value !== PLACE_HOLDER) {
		if (
			obj.thresholdKey ||
			obj.thresholdValue ||
			obj.thresholdValue === 0
		) {
			const thValue = obj.thresholdKey
				? data[obj.thresholdKey]
				: obj.thresholdValue;
			const isValueLess = value < thValue;
			const isValueGreater = value > thValue;

			// Assigned to th class instead of final class so that green/red applies only to value and not whole object including pre/suff
			if (obj.reverseThreshold) {
				thClass +=
					EMPTY_SPACE + (isValueGreater ? RED_CLASS : GREEN_CLASS);
			} else {
				thClass +=
					EMPTY_SPACE + (isValueLess ? RED_CLASS : GREEN_CLASS);
			}

			if (obj.showIcon) {
				let iconName = isValueLess ? RED_DOWN_ICON : GREEN_UP_ICON;
				let iconClass =
					displayType === "SECONDARY" ? SECONDARY_ICON_CLASS : "";

				if (obj.reverseThreshold) {
					iconName = isValueGreater ? RED_DOWN_ICON : GREEN_UP_ICON;
					iconClass += EMPTY_SPACE + "zf-icon-react-reverse";
				}

				icon = { name: iconName, class: iconClass };
			}
		}

		if (obj.showAbsoluteValue && Number.isFinite(value)) {
			value = Math.abs(value);
		}

		if (obj.valueType === "PERCENTAGE") {
			value = (value as string) + "%";
		}
	}

	let recursivePrefix =
		obj.prefix &&
		(typeof obj.prefix === "string"
			? obj.prefix
			: getTransformedColumn(obj.prefix, data));
	let recursiveSuffix =
		obj.suffix &&
		(typeof obj.suffix === "string"
			? obj.suffix
			: getTransformedColumn(obj.suffix, data));

	return {
		class: finalClass,
		thClass,
		value,
		icon,
		prefix: recursivePrefix,
		suffix: recursiveSuffix,
	};
};

const transformMetaConfig = (tab: IMetaCardTab, data: object) => {
	const metaCardTabKey = tab.key ? tab.key + "_" : "";
	let transformedConfig = tab.config.map((row) => {
		return row.map((column) => {
			const header = column.header;
			const headerLegend = column.headerLegend;
			let toolTipKey = column.toolTipKey || "";
			const multipleDefinitonAvailable = KEYS_WITH_DIFFERENT_DEFINITION.find(
				(item) => item === toolTipKey
			);
			if (multipleDefinitonAvailable) {
				if (metaCardTabKey) {
					toolTipKey = metaCardTabKey + toolTipKey;
				} else {
					toolTipKey = header + "_" + toolTipKey;
				}
			}
			const valueArray: ITransformedColumn[] = column.valueArray
				? column.valueArray.map((valArrObj) =>
						getTransformedColumn(valArrObj, data)
				  )
				: [];

			return {
				toolTipKey,
				valueArray,
				header,
				headerLegend,
				...getTransformedColumn(column, data),
			};
		});
	});

	if (tab.columnsPerRow) {
		const columns = [];

		transformedConfig.forEach((row: IColumnObject[]) =>
			columns.push(...row)
		);

		const rows = chunk(columns, tab.columnsPerRow);
		const lastRow = rows[rows.length - 1];

		while (lastRow.length < tab.columnsPerRow) {
			lastRow.push({ header: "" });
		}

		transformedConfig = rows;
	}

	let globalConfig: Array<IGlobalConfigObject> = [];

	if (tab.globalData) {
		globalConfig = tab.globalData.map((g) => {
			return { prefix: g.prefix, value: data[g.key], suffix: g.suffix };
		});
	}

	return { globalConfig, multiStyleCardConfig: transformedConfig };
};

export type MultiStyleCardConfig = ReturnType<typeof transformMetaConfig>;
